import { Component, ElementRef, Input, Renderer2, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';
import { colors } from 'src/app/colors';
import { ProjectService } from 'src/app/Services/project.service';
import { TourConfigService } from 'src/app/Services/tour-config.service';

@Component({
  selector: 'application-usage',
  templateUrl: './application-usage-chart.component.html',
  styleUrls: ['./application-usage-chart.component.css'],
  standalone: false
})
export class ApplicationUsageChartComponent {
  @ViewChild('webBrowserShowingChartCanvas') websiteUsageChartRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('applicationUsageChart') applicationUsageChart: ElementRef<HTMLCanvasElement>;

  @Input() data: any;
  @Input() labels: any;
  @Input() isChartWebBrowserUser: boolean;
  @Input() chartdata: any = [];
  displayData: any = [];
  @Input() chartDataForWebsiteUsage: any;

  chart: Chart;
  websiteUsageChart: Chart;
  isdata = true;
  maxTime = '0';
  maxTimeWebsiteUsage = '0';

  constructor(
    private renderer: Renderer2,
    private _projectService: ProjectService,
    private _tourService: TourConfigService
  ) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    if (this.isChartWebBrowserUser) {
      this.createWebsiteUsageChart();
    } else {
      this.createApplicationUsageChart();
    }

    this.setSubjects();
  }

  setSubjects(): void {
    this._projectService.ApplicationUSage.subscribe((value: any) => {
      this.chartdata = value;
      this.displayData = value;
      this.maxTime = value.display ? this.getTotalTime(value.data) : '00:00';
      this.updateDoughnutChart(this.chart, this.chartdata, this.convertIntoTime.bind(this), "No used application");
    });

    this._projectService.webSiteUsage.subscribe((value: any) => {
      this.chartDataForWebsiteUsage = value;
      this.maxTimeWebsiteUsage = value.display ? this.getTotalTime(value.data) : '00:00';
      this.updateDoughnutChart(this.websiteUsageChart, this.chartDataForWebsiteUsage, this.convertIntoTimeForWebsiteUsage.bind(this), "No website usage");
    });
  }

  createApplicationUsageChart(): void {
    this.chart = this.createDoughnutChart(
      this.applicationUsageChart,
      this.convertIntoTime.bind(this),
      "No used application"
    );
  }

  createWebsiteUsageChart(): void {
    // if (this.websiteUsageChartRef?.nativeElement) {
      this.websiteUsageChart = this.createDoughnutChart(
        this.websiteUsageChartRef,
        this.convertIntoTimeForWebsiteUsage.bind(this),
        "No website usage"
      );
    // }
  }

  private createDoughnutChart(
    canvasRef: ElementRef<HTMLCanvasElement>,
    convertFn: (index: number) => string,
    fallbackLabel: string
  ): Chart {
    const canvas = canvasRef.nativeElement;
    const ctx = canvas.getContext('2d');

    return new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels: [fallbackLabel],
        datasets: [{
          data: [100],
          backgroundColor: colors.pieChart.background,
        }]
      },
      options: {
        cutout: '75%',
        elements: { arc: { borderWidth: 8 } },
        responsive: true,
        maintainAspectRatio: true,
        animation: {
          animateRotate: true,
          animateScale: false,
          duration: 1000
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              // Suppress the auto-title (v4 shows slice label as title by default,
              // which duplicates our label callback that already includes the name)
              title: () => '',
              label: (tooltipItem) => {
                const index = tooltipItem.dataIndex;
                const label = tooltipItem.label ?? fallbackLabel;
                const value = convertFn && typeof index === 'number' ? convertFn(index) : '0h';
                return `${label}: ${value}`;
              }
            },
            backgroundColor: colors.pieChart.tooltip.backgroundColor,
            titleColor: colors.pieChart.tooltip.titleFontColor,
            bodyColor: colors.pieChart.tooltip.bodyFontColor,
            bodyFont: { size: 14 },
            titleFont: { size: 16 },
            displayColors: true,
          },
        },
      },
    } as any);
  }

  updateDoughnutChart(
    chartInstance: Chart,
    chartData: any,
    convertFn: (index: number) => string,
    fallbackLabel: string
  ): void {
    if (!chartInstance || !chartInstance.data || !chartInstance.data.datasets || !chartInstance.data.datasets[0]) {
      return;
    }

    const dataset = chartInstance.data.datasets[0];
    let displayAsEmptyState = true;

    if (chartData && chartData.data?.length > 0 && chartData.label?.length > 0 && chartData.data.length === chartData.label.length) {
      const numericalData = this.convertToMilliseconds(chartData.data);
      const allDataValuesAreZero = numericalData.every(value => value === 0);

      if (!allDataValuesAreZero) {
        chartInstance.data.labels = chartData.label;
        dataset.data = numericalData;
        dataset.backgroundColor = colors.pieChart.background;
        displayAsEmptyState = false;
      }
    }

    this.isdata = !displayAsEmptyState;

    if (displayAsEmptyState) {
      chartInstance.data.labels = [fallbackLabel];
      dataset.data = [100];
      const emptyFillColor = 'rgba(230, 230, 230, 1)';
      dataset.backgroundColor = [emptyFillColor];
    }

    chartInstance.update();
  }

  convertIntoTime(index: number): string {
    const value = this.chartdata?.data?.[index];
    if (!value) return '0h';
    const [hours = 0, minutes = 0] = value.split(":").map(Number);
    return `${this.padZeroes(hours)}:${this.padZeroes(minutes)}h`;
  }

  convertIntoTimeForWebsiteUsage(index: number): string {
    const value = this.chartDataForWebsiteUsage?.data?.[index];
    if (!value) return '0h';
    const [hours = 0, minutes = 0] = value.split(":").map(Number);
    return `${this.padZeroes(hours)}:${this.padZeroes(minutes)}h`;
  }

  convertToMilliseconds(timeArray: string[]): number[] {
    return timeArray.map(time => {
      const milliseconds = this.timeToMilliseconds(time);
      const hours = this.millisecondsToHours(milliseconds);
      return Number(hours.toFixed(2));
    });
  }

  getTotalTime(timeArray: string[]): string {
    let totalHours = 0;
    let totalMinutes = 0;
    for (const time of timeArray) {
      const [h = 0, m = 0] = time.split(':').map(Number);
      totalHours += h;
      totalMinutes += m;
    }
    totalHours += Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${this.padZeroes(totalHours)}:${this.padZeroes(minutes)}`;
  }

  padZeroes(value: number): string {
    return value.toString().padStart(2, '0');
  }

  timeToMilliseconds(timeString: string): number {
    const [hours = 0, minutes = 0, seconds = 0] = timeString.split(":").map(Number);
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }

  millisecondsToHours(milliseconds: number): number {
    return milliseconds / 3600000;
  }

  findMaxTime(timeArray: string[]): string {
    let maxTime = "00:00:00";
    for (const time of timeArray) {
      const [h = 0, m = 0, s = 0] = time.split(":").map(Number);
      const totalSeconds = h * 3600 + m * 60 + s;
      const [maxH, maxM, maxS] = maxTime.split(":").map(Number);
      const maxSeconds = maxH * 3600 + maxM * 60 + maxS;
      if (totalSeconds > maxSeconds) {
        maxTime = time;
      }
    }
    return maxTime;
  }
}